/* Existing ritual tabs + food rail, with one bounded, lifecycle-safe controller.
   Replace the old recipes.js. Do not load this alongside it. */
(() => {
  'use strict';
  const KEY = '__pbFoodRuntime';
  if (window[KEY]) { window[KEY].init(document); return; }
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion:reduce)');
  function mount(root) {
    if (instances.has(root)) return;
    const listeners = [];
    const listen = (target, type, fn, options) => {
      if (!target) return;
      target.addEventListener(type, fn, options);
      listeners.push(() => target.removeEventListener(type, fn, options));
    };
    const tabs = [...root.querySelectorAll('[data-pb-use-tab]')];
    const ritualPanels = tabs.map(tab => document.getElementById(tab.getAttribute('aria-controls'))).filter(panel => panel && root.contains(panel));
    function sizeRitualPanels() {
      const shown = ritualPanels.find(panel => !panel.hidden);
      if (!shown) return;
      const width = shown.getBoundingClientRect().width;
      if (!width) return;
      let height = 0;
      ritualPanels.forEach(panel => {
        const hidden = panel.hidden;
        const previous = panel.getAttribute('style');
        panel.style.cssText += ';position:absolute;visibility:hidden;pointer-events:none;min-height:0;width:' + width + 'px;max-width:none;';
        panel.hidden = false;
        height = Math.max(height, panel.getBoundingClientRect().height);
        panel.hidden = hidden;
        previous === null ? panel.removeAttribute('style') : panel.setAttribute('style', previous);
      });
      root.style.setProperty('--pb-ritual-min-height', Math.ceil(height) + 'px');
    }
    function select(tab) {
      if (!tab || !tabs.includes(tab)) return;
      root.style.setProperty('--ritual-progress', `${(tabs.indexOf(tab) + 1) / tabs.length * 100}%`);
      tabs.forEach(item => {
        const yes = item === tab;
        item.setAttribute('aria-selected', String(yes));
        item.tabIndex = yes ? 0 : -1;
        const panel = document.getElementById(item.getAttribute('aria-controls'));
        if (panel && root.contains(panel)) panel.hidden = !yes;
      });
    }
    tabs.forEach((tab, index) => {
      listen(tab, 'click', () => select(tab));
      listen(tab, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        select(tabs[nextIndex]);
        tabs[nextIndex].focus({ preventScroll: true });
      });
    });
    root.querySelectorAll('[data-pb-ritual-next]').forEach(button => listen(button, 'click', () => {
      const next = tabs.find(tab => tab.dataset.pbUseTab === button.dataset.pbRitualNext);
      if (next) { select(next); next.focus({ preventScroll: true }); }
    }));
    select(tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    sizeRitualPanels();
    listen(window, 'resize', sizeRitualPanels, { passive: true });
    document.fonts?.ready.then(() => { if (root.isConnected) sizeRitualPanels(); });
    const track = root.querySelector('.pb-food-track');
    const pause = root.querySelector('[data-pb-food-pause]');
    const prev = root.querySelector('[data-pb-food-prev]');
    const next = root.querySelector('[data-pb-food-next]');
    const saved = window[KEY]?.states?.get(root);
    let manualPause = saved?.manualPause || false;
    let hovered = false;
    let touching = false;
    let visible = false;
    let destroyed = false;
    let previousTime = null;
    let fraction = 0;
    let frame = 0;
    let cardStep = 0;
    let overflow = false;
    const globalPause = () => document.documentElement.classList.contains('dm-paused');
    const still = () => manualPause || reduced.matches || globalPause();
    const intersects = () => {
      if (!track || track.hidden) return false;
      const r = track.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
    };
    const running = () => !destroyed && !!track && visible && overflow && !still() && !document.hidden && !hovered && !touching && !track.contains(document.activeElement) && !track.querySelector('details[open]');
    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      previousTime = null;
    };
    const requestDraw = () => { if (!frame && running()) frame = requestAnimationFrame(draw); };
    function sync() {
      if (destroyed) return;
      if (pause) {
        pause.disabled = reduced.matches || globalPause() || !overflow;
        pause.setAttribute('aria-pressed', String(still() || !overflow));
        pause.textContent = reduced.matches ? 'Reduced motion' : globalPause() ? 'Motion paused' : !overflow ? 'All scenes visible' : manualPause ? 'Play slideshow' : 'Pause slideshow';
      }
      if (prev) prev.disabled = !overflow;
      if (next) next.disabled = !overflow;
      root.dataset.pbFoodRunning = String(running());
      if (!running()) stop();
      requestDraw();
    }
    function measure() {
      if (!track) return;
      const first = track.firstElementChild;
      cardStep = first ? first.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0) : 0;
      overflow = cardStep > 0 && track.children.length > 1 && track.scrollWidth > track.clientWidth + 1;
      visible = intersects();
      sync();
    }
    function draw(now) {
      frame = 0;
      if (!root.isConnected) { cleanup(); return; }
      if (!running()) { previousTime = null; return; }
      if (!intersects()) { visible = false; sync(); return; }
      const delta = previousTime === null ? 0 : Math.max(0, Math.min(now - previousTime, 50));
      previousTime = now;
      fraction += delta * .023;
      const pixels = Math.floor(fraction);
      fraction -= pixels;
      if (pixels) {
        let target = track.scrollLeft + pixels;
        if (target >= cardStep) {
          track.append(track.firstElementChild);
          target -= cardStep;
        }
        track.scrollLeft = target;
      }
      requestDraw();
    }
    function move(direction) {
      if (!track || !overflow) return;
      manualPause = true;
      sync();
      const end = Math.max(0, track.scrollWidth - track.clientWidth);
      let target = track.scrollLeft + direction * cardStep;
      if (target > end + 4) target = 0;
      if (target < -4) target = end;
      track.scrollTo({ left: Math.max(0, Math.min(end, target)), behavior: reduced.matches || globalPause() ? 'auto' : 'smooth' });
    }
    listen(pause, 'click', () => { if (!pause.disabled) { manualPause = !manualPause; sync(); } });
    listen(prev, 'click', () => move(-1));
    listen(next, 'click', () => move(1));
    if (track) {
      listen(track, 'keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          manualPause = true;
          sync();
          track.scrollLeft = event.key === 'Home' ? 0 : Math.max(0, track.scrollWidth - track.clientWidth);
        } else move(event.key === 'ArrowRight' ? 1 : -1);
      });
      listen(track, 'mouseenter', () => { hovered = true; sync(); });
      listen(track, 'mouseleave', () => { hovered = false; sync(); });
      listen(track, 'pointerdown', () => { touching = true; manualPause = true; sync(); }, { passive: true });
      listen(window, 'pointerup', () => { touching = false; sync(); }, { passive: true });
      listen(window, 'pointercancel', () => { touching = false; sync(); }, { passive: true });
      listen(track, 'focusin', sync);
      listen(track, 'focusout', event => {
        // focusout fires before document.activeElement changes; sync in a microtask.
        queueMicrotask(() => { if (!destroyed) sync(); });
      });
      listen(track, 'toggle', sync, true);
    }
    const observer = track && 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      const entry = entries.find(item => item.target === track);
      if (entry) visible = entry.isIntersecting && intersects();
      sync();
    }, { threshold: 0, rootMargin: '0px' }) : null;
    const classes = new MutationObserver(sync);
    classes.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    const resize = track && 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    if (resize) { resize.observe(track); if (track.firstElementChild) resize.observe(track.firstElementChild); }
    listen(window, 'resize', measure, { passive: true });
    if (!observer) listen(window, 'scroll', measure, { passive: true });
    listen(document, 'visibilitychange', sync);
    listen(reduced, 'change', sync);
    function cleanup() {
      if (destroyed) return;
      window[KEY]?.states?.set(root, { manualPause });
      destroyed = true;
      stop();
      observer?.disconnect();
      classes.disconnect();
      resize?.disconnect();
      listeners.forEach(remove => remove());
      delete root.dataset.pbReady;
      delete root.dataset.pbFoodRunning;
      instances.delete(root);
    }
    instances.set(root, cleanup);
    root.dataset.pbReady = 'true';
    observer?.observe(track);
    measure();
    sync();
  }
  function init(scope = document) {
    instances.forEach((cleanup, root) => { if (!root.isConnected) cleanup(); });
    const enclosing = scope.closest?.('[data-pb-use]');
    if (enclosing && enclosing !== scope) { instances.get(enclosing)?.(); mount(enclosing); return; }
    if (scope.matches?.('[data-pb-use]')) mount(scope);
    scope.querySelectorAll('[data-pb-use]').forEach(mount);
  }
  function destroy(scope = document) {
    instances.forEach((cleanup, root) => {
      if (scope === document || scope === root || scope.contains?.(root) || root.contains?.(scope)) cleanup();
    });
  }
  window[KEY] = { init, destroy, states: new WeakMap() };
  window.initPBFood = mount;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  window.addEventListener('pagehide', () => destroy());
  window.addEventListener('pageshow', () => init());
})();
